/**
 * 共通レイアウトコンポーネント
 *
 * LaravelでいうBladeの@extends('layouts.app')に相当
 * 全ページ共通のヘッダー・ナビゲーションを提供します
 */

import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Props の型定義
interface LayoutProps {
  children: React.ReactNode;  // 子要素（各ページのコンテンツ）
}

// ナビゲーションリンクの定義（roleで表示制御）
const navLinks = [
  { path: '/home', label: 'ホーム', roles: null },
  { path: '/patients', label: '患者一覧', roles: ['FACILITY_ADMIN', 'EXAMINER'] as string[] },
  { path: '/facilities', label: '施設一覧', roles: ['SYSTEM_ADMIN'] as string[] },
  { path: '/users', label: 'ユーザー管理', roles: ['SYSTEM_ADMIN', 'FACILITY_ADMIN'] as string[] },
  { path: '/settings', label: '設定', roles: ['FACILITY_ADMIN', 'EXAMINER'] as string[] },
];

function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { user, logout } = useAuth();

  const roleLabel = user?.role === 'SYSTEM_ADMIN' ? 'システム管理者'
    : user?.role === 'FACILITY_ADMIN' ? '施設管理者'
    : '検査者';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ========================================
          ヘッダー
          LaravelでいうBladeの@section('header')に相当
          ======================================== */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* ロゴ・タイトル */}
            <Link to="/home" className="flex items-center space-x-2">
              <span className="text-xl font-bold text-gray-800">
                LineBisect
              </span>
              <span className="text-sm text-gray-500">
                線分二等分課題
              </span>
            </Link>

            {/* ユーザー情報・ログアウト */}
            <div className="flex items-center space-x-3 text-sm">
              <span className="text-gray-600">
                {user?.facility?.name || ''}
              </span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">
                {user?.name}
                <span className="ml-1 text-xs text-gray-400">({roleLabel})</span>
              </span>
              <button
                onClick={logout}
                className="ml-2 px-3 py-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ========================================
          ナビゲーション
          LaravelでいうBladeの@include('partials.nav')に相当
          ======================================== */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4">
            {navLinks
              .filter((link) => {
                if (link.path === '/home' && user?.role === 'SYSTEM_ADMIN') {
                  return false;
                }
                return !link.roles || (user && link.roles.includes(user.role));
              })
              .map((link) => {
              const isActive = location.pathname === link.path ||
                              location.pathname.startsWith(link.path + '/');

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    px-3 py-4 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* ========================================
          メインコンテンツ
          LaravelでいうBladeの@yield('content')に相当
          ======================================== */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {children}
      </main>

      {/* ========================================
          フッター
          ======================================== */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-4 text-center text-sm text-gray-500">
          LineBisect v0.1.0
        </div>
      </footer>
    </div>
  );
}

export default Layout;
